import { z } from 'zod';

const configSchema = z.object({
  VITE_ROOT_API_URL: z.string().url(),
  VITE_TRACE_URL: z.string().url(),
  VITE_ANALYTICS_URL: z.string().url(),
  VITE_MEDIA_API_URL: z.string().url(),
  VITE_MICROSOFT_SIA_CLIENT_ID: z.string(),
  VITE_MICROSOFT_SIA_REDIRECT_URI: z.string().url(),
  VITE_MICROSOFT_SIA_OAUTH_URL: z.string().url(),
  SENTRY_DEBUG: z.boolean(),
  VITE_ENVIRONMENT: z.enum([
    'local',
    'staging',
    'preprod',
    'preprod-us',
    'release',
    'release-us',
    'demo'
  ]),
  VITE_ONE_ACCOUNT_AXA_CLIENT_ID: z.string(),
  VITE_ONE_ACCOUNT_AXA_REDIRECT_URI: z.string(),
  VITE_ONE_ACCOUNT_AXA_OAUTH_URL: z.string(),
  VITE_IS_US_REGION: z.boolean(),
  ROOT_URL: z.string()
});

type Config = z.infer<typeof configSchema>;

const configs: Record<string, Config> = {
  local: {
    VITE_ROOT_API_URL: 'http://account-api.local.stratumn.com:4200',
    VITE_TRACE_URL: 'http://trace.local.stratumn.com:4101',
    VITE_ANALYTICS_URL: 'http://analytics.local.stratumn.com:4802',
    VITE_MEDIA_API_URL: 'http://media-api.local.stratumn.com:4500',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: '3529320d',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI: 'http://localhost:4201/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.stg.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: '1ebfba54-e34c-448a-b7ee-19e9847c911e',
    VITE_MICROSOFT_SIA_REDIRECT_URI:
      'https://account.staging.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'local',
    VITE_IS_US_REGION: false,
    ROOT_URL: 'local.stratumn.com'
  },
  staging: {
    VITE_ROOT_API_URL: 'https://account-api.staging.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.staging.stratumn.com',
    VITE_TRACE_URL: 'https://trace.staging.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.staging.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: '3529320d',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI:
      'https://account.staging.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.stg.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: '1ebfba54-e34c-448a-b7ee-19e9847c911e',
    VITE_MICROSOFT_SIA_REDIRECT_URI:
      'https://account.staging.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: true,
    VITE_ENVIRONMENT: 'staging',
    VITE_IS_US_REGION: false,
    ROOT_URL: 'staging.stratumn.com'
  },
  preprod: {
    VITE_ROOT_API_URL: 'https://account-api.preprod.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.preprod.stratumn.com',
    VITE_TRACE_URL: 'https://trace.preprod.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.preprod.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: 'cvo041ah',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI:
      'https://account.preprod.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: 'fa6f3542-ba61-41a2-992c-187ac8264b45',
    VITE_MICROSOFT_SIA_REDIRECT_URI:
      'https://account.preprod.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'preprod',
    VITE_IS_US_REGION: false,
    ROOT_URL: 'preprod.stratumn.com'
  },
  preprod_us: {
    VITE_ROOT_API_URL: 'https://account-api.preprod.us.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.preprod.us.stratumn.com',
    VITE_TRACE_URL: 'https://trace.preprod.us.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.preprod.us.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: 'cvo041ah',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI:
      'https://account.preprod.us.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: 'fa6f3542-ba61-41a2-992c-187ac8264b45',
    VITE_MICROSOFT_SIA_REDIRECT_URI:
      'https://account.preprod.us.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'preprod-us',
    VITE_IS_US_REGION: true,
    ROOT_URL: 'preprod.us.stratumn.com'
  },
  release: {
    VITE_ROOT_API_URL: 'https://account-api.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.stratumn.com',
    VITE_TRACE_URL: 'https://trace.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: '3529320d',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI: 'https://account.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: '1d5d68ab-4fb2-48c6-a243-0d5857d4650f',
    VITE_MICROSOFT_SIA_REDIRECT_URI: 'https://account.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'release',
    VITE_IS_US_REGION: false,
    ROOT_URL: 'stratumn.com'
  },
  release_us: {
    VITE_ROOT_API_URL: 'https://account-api.us.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.us.stratumn.com',
    VITE_TRACE_URL: 'https://trace.us.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.us.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: '3529320d',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI: 'https://account.us.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: '1d5d68ab-4fb2-48c6-a243-0d5857d4650f',
    VITE_MICROSOFT_SIA_REDIRECT_URI: 'https://account.us.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    SENTRY_DEBUG: false,
    VITE_ENVIRONMENT: 'release-us',
    VITE_IS_US_REGION: true,
    ROOT_URL: 'us.stratumn.com'
  },
  demo: {
    VITE_ROOT_API_URL: 'https://account-api.demo.stratumn.com',
    VITE_MEDIA_API_URL: 'https://media-api.demo.stratumn.com',
    VITE_TRACE_URL: 'https://trace.demo.stratumn.com',
    VITE_ANALYTICS_URL: 'https://analytics.demo.stratumn.com',
    VITE_ONE_ACCOUNT_AXA_CLIENT_ID: '3529320d',
    VITE_ONE_ACCOUNT_AXA_REDIRECT_URI:
      'https://account.demo.stratumn.com/oauth',
    VITE_ONE_ACCOUNT_AXA_OAUTH_URL:
      'https://onelogin.stg.axa.com/as/authorization.oauth2',
    VITE_MICROSOFT_SIA_CLIENT_ID: 'abf05e5d-b108-49ab-a84e-02c89e7571f3',
    VITE_MICROSOFT_SIA_REDIRECT_URI: 'https://account.demo.stratumn.com/oauth',
    VITE_MICROSOFT_SIA_OAUTH_URL:
      'https://login.microsoftonline.com/af1bbf3d-7aa3-4e3f-8173-c8889d944e6a/oauth2/v2.0/authorize',
    VITE_ENVIRONMENT: 'demo',
    SENTRY_DEBUG: false,
    VITE_IS_US_REGION: false,
    ROOT_URL: 'demo.stratumn.com'
  }
};

type EnvName = keyof typeof configs;

const getConfig = () => {
  const hostname = window.location.hostname;
  let envConfig: Config;

  if (hostname.includes('staging')) {
    envConfig = configs.staging;
  } else if (hostname.includes('preprod.us')) {
    envConfig = configs.preprod_us;
  } else if (hostname.includes('preprod')) {
    envConfig = configs.preprod;
  } else if (hostname.includes('.us.')) {
    envConfig = configs.release_us;
  } else if (hostname.includes('local')) {
    envConfig = configs.local;
  } else if (hostname.includes('demo')) {
    envConfig = configs.demo;
  } else {
    envConfig = configs.release;
  }

  const mode = import.meta.env.VITE_MODE as EnvName | undefined;
  if (mode && mode in configs) {
    envConfig = configs[mode];
  }

  const mergedConfig = Object.keys(envConfig).reduce(
    (acc, key) => {
      const envValue = import.meta.env[key as keyof ImportMeta];
      acc[key] = envValue !== undefined ? envValue : envConfig[key];
      return acc;
    },
    {} as Record<string, unknown>
  );

  const validatedConfig = configSchema.safeParse(mergedConfig);

  if (validatedConfig.error) {
    console.info('env', validatedConfig);
    throw validatedConfig.error;
  }

  return validatedConfig.data;
};

export const env = getConfig();
